import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { usePortalAuth } from "../PortalAuthContext";
import { portalNotificationsApi } from "@/api/portalNotifications";
import { ApiError } from "@/api/client";

// External Collection → Notifications — shared unread-count source for the
// portal bell (top bar) and the nav badge (sidebar). Polls every 30s while
// a portal session is active (v1 — no websockets needed); the feed tab
// calls refresh() right after it marks items read so the badge clears
// without waiting for the next poll.
//
// Two guards (perf PR): a 401 clears the auth state — a logged-out/expired
// tab must STOP polling, not 401 forever — and the poll skips ticks while
// the tab is hidden (catch-up fires on visibilitychange).

const POLL_INTERVAL_MS = 30_000;

interface PortalNotificationsContextValue {
  unreadCount: number;
  refresh: () => Promise<void>;
}

const PortalNotificationsContext = createContext<PortalNotificationsContextValue>({
  unreadCount: 0,
  refresh: async () => {},
});

export function PortalNotificationsProvider({ children }: { children: ReactNode }) {
  const auth = usePortalAuth();
  const isAuthed = Boolean(auth.me);
  const [unreadCount, setUnreadCount] = useState(0);

  async function refresh() {
    if (!isAuthed) return;
    try {
      setUnreadCount(await portalNotificationsApi.unreadCount());
    } catch {
      // Non-critical: a transient failure shouldn't break the chrome.
    }
  }

  useEffect(() => {
    if (!isAuthed) {
      setUnreadCount(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const count = await portalNotificationsApi.unreadCount();
        if (!cancelled) setUnreadCount(count);
      } catch (err) {
        // The session cookie was invalidated out from under this tab —
        // clearing `me` flips isAuthed, which tears down this interval via
        // the effect cleanup (this poller rides the staff api client, so
        // the portal client's global 401 handler never sees it).
        if (err instanceof ApiError && err.status === 401 && !cancelled) {
          auth.setMe(null);
        }
        // Other failures are non-critical; next tick retries.
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    void load();
    const handle = window.setInterval(() => void load(), POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  return (
    <PortalNotificationsContext.Provider value={{ unreadCount, refresh }}>
      {children}
    </PortalNotificationsContext.Provider>
  );
}

export function usePortalNotifications() {
  return useContext(PortalNotificationsContext);
}
